import { P } from 'components'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingAnimation from 'components/LoadingAnimation'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import { useState } from 'react'
import { ArrowLeft, Edit2 } from 'react-feather'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  Alert,
  Card,
  CardBody,
  Col,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap'
import { URL_406 } from 'utils/pathUrl'
import { useFetchSettingsQuery } from '../settingApiSlice'
import ModalEditCompanyInfo from './ModalEditCompanyInfo'

const CompanySetting = ({ action, subject }) => {
  const navigate = useNavigate()
  const { data, isLoading, isSuccess, error, refetch } = useFetchSettingsQuery()
  const ability = useAbilityAccess({ action, subject })
  const { individualUser, ownerData: reduxOwnerData } = useSelector(
    (s) => s.dashboard,
  )

  const [isOpenEditInfo, setIsOpenEditInfo] = useState(false)

  const toggleEditInfo = () => setIsOpenEditInfo(!isOpenEditInfo)

  // 1. User must have business_company manage ability (opposite of: user tanpa business_company manage -> 406)
  const canManageCompany = Boolean(ability)

  // 2. User must have an active partnership (opposite of: user pribadi yg login tp gak punya partnership -> 404)
  const isPersonalWithoutPartnership = Boolean(
    error?.status === 404 ||
      (individualUser &&
        !data?.ownerData?.companyName &&
        !reduxOwnerData?.companyName) ||
      (!isLoading && isSuccess && !data?.ownerData?.companyName),
  )

  const hasPartnership =
    !isPersonalWithoutPartnership &&
    !error &&
    Boolean(data?.ownerData?.companyName || reduxOwnerData?.companyName)

  // Tombol Ubah Profil hanya tampil jika punya ability business_company manage DAN memiliki partnership
  const canEditProfile = canManageCompany && hasPartnership

  if (!ability) {
    return <Navigate to={URL_406} replace={true} />
  }

  const companyLogo =
    data?.ownerData?.companyLogo || reduxOwnerData?.companyLogo

  const companyName =
    data?.ownerData?.companyName || reduxOwnerData?.companyName || '-'

  const companyShortName =
    data?.ownerData?.companyShortName ||
    reduxOwnerData?.companyShortName ||
    '-'

  // const companyMidName =
  //   data?.ownerData?.companyMidName ||
  //   reduxOwnerData?.companyMidName ||
  //   '-'

  const rawColorCode =
    data?.ownerData?.companyColorCode ||
    reduxOwnerData?.companyColorCode

  const formattedColor = rawColorCode
    ? rawColorCode.startsWith('#')
      ? rawColorCode
      : `#${rawColorCode}`
    : null

  return (
    <>
      <ModalEditCompanyInfo
        toggle={toggleEditInfo}
        modal={isOpenEditInfo}
        ownerData={data?.ownerData || reduxOwnerData}
        refetchSettings={refetch}
        centered={true}
        keyboard={false}
        backdrop="static"
      />

      <CardBody>

        <ListGroup flush>
          <ListGroupItem
            className="py-4 px-3"
            style={{
              backgroundColor: 'white',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ArrowLeft
                  size={20}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    navigate(-1)
                  }}
                />
                <div>
                  <P size="xlarge">Profil perusahaan</P>
                </div>
              </div>
            </div>
          </ListGroupItem>
          <div className="p-3">
            {isPersonalWithoutPartnership && (
              <Alert color="warning" className="d-flex align-items-center gap-2 mb-3">
                <span>
                  Akun Anda terdaftar sebagai akun pribadi dan tidak memiliki profil partnership perusahaan.
                  Fitur profil perusahaan hanya dapat diubah oleh akun yang memiliki partnership.
                </span>
              </Alert>
            )}
            <Card
              className="p-3"
              style={{
                border: 'none',
              }}
            >
              {/* Profil & Logo Perusahaan Section */}
              <ListGroupItem className="py-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <P tag="p" bold>
                      Profil perusahaan Anda di sini
                    </P>
                    <P tag="p" className="text-muted text-sm mb-0">
                      Kelola logo, nama, singkatan, dan warna identitas perusahaan
                    </P>
                  </div>
                  {canEditProfile && (
                    <CustomButton
                      color="primary"
                      outline
                      size="sm"
                      testId="company-info-edit-btn"
                      onClick={toggleEditInfo}
                    >
                      <Edit2 size={14} style={{ marginRight: 4 }} />
                      Ubah Profil
                    </CustomButton>
                  )}
                </div>
              </ListGroupItem>
              <ListGroupItem className="py-4">
                <Row className="align-items-center">
                  <Col sm="4">Logo</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      companyLogo ? (
                        <div
                          style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8f9fa',
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={companyLogo}
                            alt="Logo perusahaan"
                            data-testid="company-logo-preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-muted">Belum ada logo</span>
                      )
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
              <ListGroupItem className="py-4">
                <Row>
                  <Col sm="4">Nama perusahaan</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      companyName
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
              <ListGroupItem className="py-4">
                <Row>
                  <Col sm="4">Singkatan</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      companyShortName
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
              {/* <ListGroupItem className="py-4">
                <Row>
                  <Col sm="4">Nama sedang</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      companyMidName
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem> */}
              <ListGroupItem className="py-4">
                <Row className="align-items-center">
                  <Col sm="4">Kode warna</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      formattedColor ? (
                        <div className="d-flex align-items-center gap-2">
                          {(() => {
                            const clean = formattedColor.replace(/^#/, '')
                            const hasAlpha = clean.length === 8 && clean.slice(6, 8).toUpperCase() !== 'FF'
                            const isFullTransparent = clean === '00000000' || clean.endsWith('00') || formattedColor.toLowerCase() === 'transparent'
                            const opacityPercent = hasAlpha ? Math.round((parseInt(clean.slice(6, 8), 16) / 255) * 100) : null

                            return (
                              <>
                                <span
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 4,
                                    border: '1px solid #ced4da',
                                    display: 'inline-block',
                                    backgroundColor: formattedColor,
                                    backgroundImage:
                                      hasAlpha || isFullTransparent
                                        ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                                        : 'none',
                                    backgroundSize: '6px 6px',
                                    backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0px',
                                  }}
                                />
                                <span className="fw-medium">
                                  {formattedColor}
                                  {isFullTransparent && (
                                    <span className="text-muted text-xs ms-1 fw-normal">(Transparan Penuh)</span>
                                  )}
                                  {!isFullTransparent && hasAlpha && (
                                    <span className="text-muted text-xs ms-1 fw-normal">(Transparan {opacityPercent}%)</span>
                                  )}
                                </span>
                              </>
                            )
                          })()}
                        </div>
                      ) : (
                        <span className="text-muted">Belum diatur</span>
                      )
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
              {/* new section */}
              <ListGroupItem className="py-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    {/* <P size="xlarge">Akun bank</P> */}
                    <P tag="p" bold>
                      Akun bank yang Anda gunakan di sini
                    </P>
                  </div>
                </div>
              </ListGroupItem>
              <ListGroupItem className="py-4">
                <Row>
                  <Col sm="4">Akun bank</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      data?.ownerData?.bankLogo ? (
                        <span className="me-2">
                          <img
                            src={data?.ownerData?.bankLogo}
                            alt="logo-bca"
                            style={{ width: '30px', height: '30px' }}
                            className="me-2"
                          />
                          <span className="fw-medium">
                            {data?.ownerData?.bankName ?? '-'}
                          </span>
                        </span>
                      ) : (
                        <span className="me-2">
                          <span>Logo Bank Tidak Ditemukan - </span>
                          <span className="fw-medium">
                            {data?.ownerData?.bankName ?? '-'}
                          </span>
                        </span>
                      )
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
              <ListGroupItem className="py-4">
                <Row>
                  <Col sm="4">No rekening</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      data?.ownerData?.bankAccountNumber
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
              <ListGroupItem className="py-4">
                <Row>
                  <Col sm="4">Nama pemilik rekening</Col>
                  <Col sm="8">
                    {!isLoading && isSuccess ? (
                      data?.ownerData?.bankAccountName
                    ) : (
                      <LoadingAnimation size="sm" />
                    )}
                  </Col>
                </Row>
              </ListGroupItem>
            </Card>
          </div>
        </ListGroup>
      </CardBody>
    </>
  )
}

export default CompanySetting
